import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { RestrictedBadge } from './data-protection/RetentionBadge'
import { Link, useParams, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CHUNKED_UPLOAD_THRESHOLD, ChunkedUploadError, type DocumentUploadProgress } from '../api/chunked-upload'
import { useDocuments, useAssetsByUids, useAssetTypes, useDocumentTypes, useUploadDocument, useDeleteDocument, useBulkDocuments, useAssetDocumentFolders } from '../hooks/useQueries'
import { DocumentFolderTree } from './documents/DocumentFolderTree'
import { groupDocumentsByFolder, type GroupableDocument } from '../lib/document-folders'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { useMemberKind } from '../contexts/PermissionsContext'
import { useAttentionCounts } from '../hooks/useAttentionCounts'
import { useApi } from '../api/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { BulkActionsBar } from './BulkActionsBar'
import { collectDroppedFiles, filesFromFileList, appendOriginalPath, type DroppedFile } from '../lib/dropped-files'
import { ImportSelectField } from './imports/ImportSelect'
import { ImportFilterBadge } from './imports/ImportFilterBadge'
import { ProcessingBlockersBanner } from './jobs/ProcessingBlockersBanner'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from './ui/dialog'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import type { components } from '../api/schema'
import { AppIcon } from './ui/icon'
import {
    mdiRefresh,
    mdiUpload,
    mdiClose,
    mdiCheckCircle,
    mdiAlertCircle,
    mdiProgressClock,
    mdiFileImage,
    mdiFilePdfBox,
    mdiFileVideo,
    mdiFileMusic,
    mdiFileExcel,
    mdiFileWord,
    mdiFilePowerpoint,
    mdiFileCode,
    mdiArchive,
    mdiFile,
    mdiDownload,
    mdiDelete,
    mdiEye,
    mdiEmail,
    mdiSwapHorizontal,
    mdiFolderMove,
    mdiRobotOutline,
    mdiShieldAccount,
    mdiLightbulbOnOutline,
    mdiCallSplit,
    mdiCheckDecagramOutline,
    mdiFolderZipOutline,
    mdiFileDocumentMultiple,
    mdiFolderOutline,
    mdiFolderOpenOutline,
    mdiFolderQuestionOutline,
    mdiChevronDown,
    mdiChevronRight
} from '@mdi/js'
import { apiBaseUrl } from '../api/client'
import { Card } from './ui/card'
import { AssetTypeTile } from './assets/AssetTypeTile'

type Document = components['schemas']['Document']

// Selectable page sizes for the documents list (spec: 20 / 100 / 200 / 500).
const PAGE_SIZE_OPTIONS = [20, 100, 200, 500] as const


interface UploadFile {
    file: File
    id: string
    status: 'pending' | 'uploading' | 'success' | 'failed'
    progress: number
    /** Set once a chunked upload's bytes are all sent and the server assembles them. */
    assembling?: boolean
    retryCount: number
    error?: string
    documentTypeUid?: string
    /** Relative path inside a dropped folder — provenance, spec 2026-08-16-04 §3. */
    originalPath?: string
}

// Custom component to handle authenticated thumbnail loading
function DocumentIcon({ document, org }: { document: Document; org: string }) {
    const { file_mime_type, uid, name } = document
    const [thumbFailed, setThumbFailed] = useState(false)

    const isImageOrPdf = (mimeType: string | undefined) => {
        if (!mimeType) return false
        return mimeType.startsWith('image/') || mimeType === 'application/pdf'
    }

    const getFileTypeIcon = (mimeType: string | undefined) => {
        if (!mimeType) return mdiFile

        // Images
        if (mimeType.startsWith('image/')) return mdiFileImage

        // PDFs
        if (mimeType === 'application/pdf') return mdiFilePdfBox

        // Videos
        if (mimeType.startsWith('video/')) return mdiFileVideo

        // Audio
        if (mimeType.startsWith('audio/')) return mdiFileMusic

        // Microsoft Office
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return mdiFileExcel
        if (mimeType.includes('word') || mimeType.includes('document')) return mdiFileWord
        if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return mdiFilePowerpoint

        // Code files
        if (mimeType.includes('javascript') || mimeType.includes('json') ||
            mimeType.includes('xml') || mimeType.includes('html') ||
            mimeType.includes('css') || mimeType.includes('text/plain')) return mdiFileCode

        // Archives
        if (mimeType.includes('zip') || mimeType.includes('tar') ||
            mimeType.includes('gzip') || mimeType.includes('rar')) return mdiArchive

        // Default
        return SECTION_ICONS.documents
    }

    // Load thumbnail using fetch with authorization header
    useEffect(() => {
        if (!isImageOrPdf(file_mime_type)) {
            return
        }
    }, [file_mime_type, uid])

    const thumbnailUrl = useMemo(() => {
        return `${apiBaseUrl}/api/v1/organizations/${org}/documents/${uid}/thumb?size=64`
    }, [uid, org])

    const hasEmailFlag = ((document as any).flags as string[] | undefined)?.includes('email')

    const iconContent = isImageOrPdf(file_mime_type) && thumbnailUrl && !thumbFailed ? (
        <img
            src={thumbnailUrl}
            alt={`${name} thumbnail`}
            className="w-12 h-12 object-cover rounded border shadow-sm"
            onError={() => setThumbFailed(true)}
        />
    ) : (
        <AppIcon tone="muted" path={getFileTypeIcon(file_mime_type)} size="xl" />
    )

    if (hasEmailFlag) {
        return (
            <div className="relative inline-block">
                {iconContent}
                <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-0.5" title="Email">
                    <AppIcon path={mdiEmail} size="xs" />
                </div>
            </div>
        )
    }

    return iconContent
}

export function DocumentsPage() {
    const { t, i18n } = useTranslation('documents')
    const navigate = useNavigate()
    const params = useParams({ strict: false })
    const search = useSearch({ from: '/$org/documents/' })
    const org = params.org || 'demo'
    const filterAssetUid = search.asset_uid || ''
    // The document's *home* contract (spec 2026-08-13-01 §5). A plain filter —
    // v1 deliberately has no "grouped by contract" view on this page.
    const filterContractUid = search.contract_uid || ''
    const filterDocumentTypeUid = search.document_type_uid || ''
    const filterFlags = search.flags || ''
    // Tri-state PII filter (spec 2026-08-13-02 §6). `undefined` = no filter.
    const filterPII = search.pii
    const filterImportUid = search.import_uid || ''
    // Deposit drill-downs from the import dashboard (spec 2026-08-16-07 §2).
    // URL-driven only: the tree and the review card are the pickers, this page
    // just carries the filters and shows a clearable chip for each.
    const filterPathPrefix = search.path_prefix || ''
    const filterUnclassified = search.unclassified === true
    const filterToValidate = search.to_validate === true
    const filterLimit = search.limit
    const proposalFilterActive = filterFlags.split(',').includes('document_type_proposal')
    // « Découpage à valider » (spec 2026-10-14-15): documents carrying a
    // pending bundle split proposal. Their rows open the prefilled split dialog.
    const splitFilterActive = filterFlags.split(',').includes('split_proposal')
    // The documents waiting for a validation, as the sidebar badge counts
    // them (issue #1739): the page says it too, so a manager who opened
    // « Documents » rather than the badge finds the queue.
    const memberKind = useMemberKind()
    const attention = useAttentionCounts(org, memberKind === 'staff')
    const toValidateCount = attention?.documents?.to_validate ?? 0
    const [selectedFiles, setSelectedFiles] = useState<DroppedFile[]>([])
    const [selectedDocumentTypeUid, setSelectedDocumentTypeUid] = useState<string>('')
    // Optional deposit session for this page-session's uploads (spec
    // 2026-08-16-05 §2). NOT persisted — it belongs to the batch being
    // uploaded, not to the user. Pre-seeded from `?import_uid=` so arriving
    // from an import dashboard lands with that import already selected.
    const [selectedImportUid, setSelectedImportUid] = useState<string>(search.import_uid || '')
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [error, setError] = useState<string | null>(null)
    const [showUploadForm, setShowUploadForm] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const [, setDragCounter] = useState(0)
    const [showUploadInterface, setShowUploadInterface] = useState(false)
    const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
    const [isProcessing, setIsProcessing] = useState(false)
    const queryClient = useQueryClient()

    // Live entity notifications: refetch the list within ~1-2s of any
    // document change anywhere in the org (upload, classification,
    // extraction, deletion), instead of waiting out the polling loop.
    useLiveSubscription({ entity: 'documents' })

    // React Query hooks
    const { data: documents = [], total: totalDocuments, isLoading: documentsLoading } = useDocuments(
        org,
        filterAssetUid || undefined,
        filterDocumentTypeUid || undefined,
        filterFlags || undefined,
        filterLimit,
        filterImportUid || undefined,
        filterContractUid || undefined,
        filterPII,
        { pathPrefix: filterPathPrefix || undefined, unclassified: filterUnclassified || undefined, toValidate: filterToValidate || undefined }
    )

    // Toggle the pending-proposal review inbox filter (URL-driven, bookmarkable).
    const toggleProposalFilter = () => {
        navigate({
            to: '/$org/documents',
            params: { org },
            search: {
                asset_uid: filterAssetUid || undefined,
                contract_uid: filterContractUid || undefined,
                document_type_uid: filterDocumentTypeUid || undefined,
                flags: proposalFilterActive ? undefined : 'document_type_proposal',
                pii: filterPII,
                import_uid: filterImportUid || undefined,
                path_prefix: filterPathPrefix || undefined,
                unclassified: filterUnclassified || undefined,
                limit: filterLimit,
            },
        })
    }

    // Toggle the « découpage à valider » queue (URL-driven, bookmarkable).
    const toggleSplitFilter = () => {
        navigate({
            to: '/$org/documents',
            params: { org },
            search: {
                asset_uid: filterAssetUid || undefined,
                contract_uid: filterContractUid || undefined,
                document_type_uid: filterDocumentTypeUid || undefined,
                flags: splitFilterActive ? undefined : 'split_proposal',
                pii: filterPII,
                import_uid: filterImportUid || undefined,
                path_prefix: filterPathPrefix || undefined,
                unclassified: filterUnclassified || undefined,
                limit: filterLimit,
            },
        })
    }

    // --- Folder grouping (spec 2026-07-27-06) --------------------------------
    // Only meaningful for a *single* selected asset: the layout is resolved
    // through that asset's site ancestor, and two assets could resolve to two
    // different layouts — which must never be merged into one tree. No asset
    // filter, or a comma-separated multi-asset value, keeps the page flat.
    const singleAssetSelected = Boolean(filterAssetUid) && !filterAssetUid.includes(',')
    const { data: folderTree } = useAssetDocumentFolders(org, singleAssetSelected ? filterAssetUid : undefined)
    const folders = useMemo(
        () => (singleAssetSelected ? folderTree?.folders ?? [] : []),
        [singleAssetSelected, folderTree]
    )
    const folderModeActive = folders.length > 0

    // Documents in the order they are displayed — the flat filter order, or the
    // folder-tree traversal order when grouping is on. Shift-range selection
    // and the row index it relies on follow what the user actually sees.
    const displayedDocuments = useMemo(() => {
        if (!folderModeActive) return documents
        const { rows, unclassified } = groupDocumentsByFolder(
            folders,
            documents as unknown as GroupableDocument[]
        )
        return [...rows.flatMap(row => row.documents), ...unclassified] as unknown as typeof documents
    }, [folderModeActive, folders, documents])

    const displayIndexByUid = useMemo(
        () => new Map(displayedDocuments.map((doc, index) => [doc.uid, index])),
        [displayedDocuments]
    )

    // Extract unique asset UIDs from documents for efficient fetching
    const documentAssetUids: string[] = useMemo(() => {
        const assetUids = documents
            .map(doc => doc.asset_uid)
            .filter((uid): uid is string => !!uid)
        return [...new Set(assetUids)]
    }, [documents])

    // Note: allAssets removed - now using API-based search for assets

    // Fetch only the specific assets referenced by documents
    const { data: documentAssets = [] } = useAssetsByUids(org, documentAssetUids)

    const { data: documentTypes = [] } = useDocumentTypes(org) // Still needed for document name display
    // The type catalogue, for the asset column's tile.
    const { data: allAssetTypes = [] } = useAssetTypes(org)
    const uploadMutation = useUploadDocument(org, selectedImportUid || undefined)

    // Follow the URL: navigating from another import's dashboard re-targets the
    // upload panel instead of silently keeping the previous deposit selected.
    useEffect(() => {
        if (search.import_uid) setSelectedImportUid(search.import_uid)
    }, [search.import_uid])
    const deleteMutation = useDeleteDocument(org)
    const bulkMutation = useBulkDocuments(org)

    // --- Bulk selection state -------------------------------------------------
    // Ephemeral (not URL-backed) set of selected document UIDs. Cleared whenever
    // the visible population changes (filters / search) or after a bulk action.
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const lastClickedIndexRef = useRef<number | null>(null)
    // The checkbox `change` event does not carry modifier keys, so we record the
    // shift state from the preceding `click` (a real MouseEvent) and read it below.
    const shiftKeyRef = useRef(false)
    const [notice, setNotice] = useState<{ tone: 'success' | 'error', message: string } | null>(null)
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
    const [docToDelete, setDocToDelete] = useState<string | null>(null)
    const [bulkTypeOpen, setBulkTypeOpen] = useState(false)
    const [bulkAssetOpen, setBulkAssetOpen] = useState(false)
    const [bulkTypeValue, setBulkTypeValue] = useState('')
    const [bulkAssetValue, setBulkAssetValue] = useState('')
    const headerCheckboxRef = useRef<HTMLInputElement>(null)

    const clearSelection = useCallback(() => {
        setSelected(new Set())
        lastClickedIndexRef.current = null
    }, [])

    // Clear the selection when the filtered/searched population changes.
    useEffect(() => {
        clearSelection()
    }, [filterAssetUid, filterDocumentTypeUid, clearSelection])

    const allSelected = documents.length > 0 && selected.size === documents.length
    const someSelected = selected.size > 0 && !allSelected

    // Native checkbox indeterminate state can only be set via the DOM node.
    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someSelected
        }
    }, [someSelected])

    const toggleAll = useCallback(() => {
        setSelected(prev => {
            if (prev.size === documents.length) {
                return new Set()
            }
            return new Set(documents.map(d => d.uid))
        })
        lastClickedIndexRef.current = null
    }, [documents])

    const toggleRow = useCallback((index: number, uid: string, shiftKey: boolean) => {
        // Capture the anchor before the state updater runs: React may defer (and, under
        // StrictMode, double-invoke) the updater, so reading the ref inside it would see
        // the value we overwrite below and collapse the range to a single row.
        const anchor = lastClickedIndexRef.current
        setSelected(prev => {
            const next = new Set(prev)
            const willSelect = !next.has(uid)
            if (shiftKey && anchor !== null) {
                const [start, end] = [anchor, index].sort((a, b) => a - b)
                for (let i = start; i <= end; i++) {
                    const rowUid = displayedDocuments[i]?.uid
                    if (!rowUid) continue
                    if (willSelect) next.add(rowUid)
                    else next.delete(rowUid)
                }
            } else if (willSelect) {
                next.add(uid)
            } else {
                next.delete(uid)
            }
            return next
        })
        lastClickedIndexRef.current = index
    }, [displayedDocuments])

    const loading = documentsLoading
    const uploading = uploadMutation.isPending

    // Computed values for upload status
    const allUploadsComplete = uploadFiles.every(f => f.status === 'success' || f.status === 'failed')
    const allUploadsSuccessful = uploadFiles.length > 0 && uploadFiles.every(f => f.status === 'success')

    const uploadSingleFile = useCallback(async (uploadFile: UploadFile) => {
        const { file, id } = uploadFile

        setUploadFiles(prev => prev.map(f =>
            f.id === id ? { ...f, status: 'uploading', progress: 0, assembling: false } : f
        ))

        try {
            const formData = new FormData()
            formData.append('file', file)
            appendOriginalPath(formData, uploadFile.originalPath)

            // Use selected document type if available
            if (uploadFile.documentTypeUid) {
                formData.append('document_type_uid', uploadFile.documentTypeUid)
            }

            // Use currently filtered asset if available
            if (filterAssetUid) {
                formData.append('asset_uid', filterAssetUid)
            }

            // A big file goes up in chunks and is then assembled server-side
            // (spec 2026-10-02-03): follow its phase. Smaller files keep the
            // single request, without progress.
            await uploadMutation.mutateAsync(formData, file.size > CHUNKED_UPLOAD_THRESHOLD ? {
                onProgress: ({ loaded, total, phase }: DocumentUploadProgress) => {
                    const progress = total > 0 ? Math.min(100, (loaded / total) * 100) : 0
                    const assembling = phase === 'assembling'
                    setUploadFiles(prev => prev.map(f => (f.id === id ? { ...f, progress, assembling } : f)))
                },
            } : undefined)

            setUploadFiles(prev => prev.map(f =>
                f.id === id ? { ...f, status: 'success', progress: 100 } : f
            ))
        } catch (err) {
            const errorMessage = err instanceof ChunkedUploadError
                ? t(err.code === 'checksum_mismatch' ? 'upload_progress.checksum_mismatch' : 'upload_progress.assembly_failed')
                : err instanceof Error ? err.message : t('properties.error_add')

            setUploadFiles(prev => {
                const fileToUpdate = prev.find(f => f.id === id)
                if (!fileToUpdate) return prev

                if (fileToUpdate.retryCount < 3) {
                    // Retry
                    return prev.map(f =>
                        f.id === id
                            ? { ...f, status: 'pending', retryCount: f.retryCount + 1, error: errorMessage }
                            : f
                    )
                } else {
                    // Max retries reached
                    return prev.map(f =>
                        f.id === id
                            ? { ...f, status: 'failed', error: errorMessage }
                            : f
                    )
                }
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentTypes, uploadMutation, filterAssetUid, t])

    // Process upload queue
    useEffect(() => {
        const processQueue = async () => {
            if (isProcessing || uploadFiles.length === 0) return

            const pendingFiles = uploadFiles.filter(f => f.status === 'pending')
            if (pendingFiles.length === 0) {
                setIsProcessing(false)
                return
            }

            setIsProcessing(true)
            const filesToProcess = pendingFiles.slice(0, 2) // Process 2 files concurrently

            // Upload files concurrently using Promise.all
            await Promise.all(filesToProcess.map(uploadFile => uploadSingleFile(uploadFile)))

            setIsProcessing(false)
        }

        processQueue()
    }, [uploadFiles, isProcessing, uploadSingleFile])

    // Auto-close upload interface when all uploads are successful
    useEffect(() => {
        if (allUploadsSuccessful && showUploadInterface) {
            const timer = setTimeout(() => {
                setShowUploadInterface(false)
                setUploadFiles([])
            }, 500)

            return () => clearTimeout(timer)
        }
    }, [allUploadsSuccessful, showUploadInterface])

    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        // filesFromFileList keeps webkitRelativePath when the picker was a
        // directory one, so a folder selection carries provenance too.
        setSelectedFiles(filesFromFileList(event.target.files))
    }

    const handleUploadClick = () => {
        if (selectedFiles.length === 0) {
            setError(t('upload.error_no_files'))
            return
        }

        const newUploadFiles: UploadFile[] = selectedFiles.map(({ file, originalPath }) => ({
            file,
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            status: 'pending',
            progress: 0,
            retryCount: 0,
            documentTypeUid: selectedDocumentTypeUid || undefined,
            originalPath,
        }))

        setUploadFiles(newUploadFiles)
        setShowUploadInterface(true)
        setShowUploadForm(false)
        setSelectedFiles([])
        setSelectedDocumentTypeUid('')
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }


    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragCounter(prev => prev + 1)
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setDragOver(true)
        }
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragCounter(prev => {
            const newCounter = prev - 1
            if (newCounter === 0) {
                setDragOver(false)
            }
            return newCounter
        })
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragOver(false)
        setDragCounter(0)

        // A dropped folder is expanded here, each file keeping the path it had
        // in the depositor's tree (spec 2026-08-16-04 §3).
        const dropped = await collectDroppedFiles(e.dataTransfer)
        if (dropped.length > 0) {
            const newUploadFiles: UploadFile[] = dropped.map(({ file, originalPath }) => ({
                file,
                id: `${file.name}-${Date.now()}-${Math.random()}`,
                status: 'pending',
                progress: 0,
                retryCount: 0,
                documentTypeUid: undefined, // Drag and drop files won't have a pre-selected type
                originalPath,
            }))

            setUploadFiles(prev => [...prev, ...newUploadFiles])
            setShowUploadInterface(true)
        }
    }


    // Row delete goes through an AlertDialog (docToDelete state), never
    // native confirm() — see front/CLAUDE.md "Confirmation Dialogs".
    const handleDelete = async () => {
        if (!docToDelete) return
        try {
            await deleteMutation.mutateAsync(docToDelete)
        } catch (err) {
            setError(err instanceof Error ? err.message : t('properties.error_update'))
        } finally {
            setDocToDelete(null)
        }
    }

    const reportBulkResult = useCallback((updated: number, skipped: number) => {
        if (skipped > 0) {
            setNotice({ tone: 'success', message: t('bulk.result_partial', { updated, skipped }) })
        } else {
            setNotice({ tone: 'success', message: t('bulk.result_updated', { count: updated }) })
        }
    }, [t])

    const handleBulkDelete = useCallback(async () => {
        try {
            const res = await bulkMutation.mutateAsync({
                document_uids: Array.from(selected),
                action: 'delete',
            })
            reportBulkResult(res?.updated ?? 0, res?.skipped?.length ?? 0)
            clearSelection()
        } catch (err) {
            setNotice({ tone: 'error', message: err instanceof Error ? err.message : t('bulk.result_error') })
        } finally {
            setBulkDeleteOpen(false)
        }
    }, [bulkMutation, selected, reportBulkResult, clearSelection, t])

    const handleBulkChangeType = useCallback(async () => {
        if (!bulkTypeValue) return
        try {
            const res = await bulkMutation.mutateAsync({
                document_uids: Array.from(selected),
                action: 'update',
                update: { document_type_uid: bulkTypeValue },
            })
            reportBulkResult(res?.updated ?? 0, res?.skipped?.length ?? 0)
            clearSelection()
        } catch (err) {
            setNotice({ tone: 'error', message: err instanceof Error ? err.message : t('bulk.result_error') })
        } finally {
            setBulkTypeOpen(false)
            setBulkTypeValue('')
        }
    }, [bulkMutation, selected, bulkTypeValue, reportBulkResult, clearSelection, t])

    const handleBulkChangeAsset = useCallback(async () => {
        if (!bulkAssetValue) return
        try {
            const res = await bulkMutation.mutateAsync({
                document_uids: Array.from(selected),
                action: 'update',
                update: { asset_uid: bulkAssetValue },
            })
            reportBulkResult(res?.updated ?? 0, res?.skipped?.length ?? 0)
            clearSelection()
        } catch (err) {
            setNotice({ tone: 'error', message: err instanceof Error ? err.message : t('bulk.result_error') })
        } finally {
            setBulkAssetOpen(false)
            setBulkAssetValue('')
        }
    }, [bulkMutation, selected, bulkAssetValue, reportBulkResult, clearSelection, t])

    const handleRefresh = () => {
        queryClient.invalidateQueries({
            predicate: query => {
                const [method, path] = query.queryKey
                return method === 'get' && path === '/api/v1/organizations/{orgCode}/documents'
            }
        })
        queryClient.invalidateQueries({
            predicate: query => {
                const [method, path] = query.queryKey
                return method === 'get' && path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
            }
        })
    }

    /**
     * The tile that says what KIND of asset a row points at (spec
     * 2026-09-17-06): a column of forty rows reads as sites / buildings /
     * units / equipment at a glance instead of as forty blue links.
     */
    const getAssetTypeFor = (assetUid: string | null | undefined) => {
        if (!assetUid) return undefined
        const asset = documentAssets.find(a => a.uid === assetUid)
        return asset?.asset_type_uid
            ? allAssetTypes.find(at => at.uid === asset.asset_type_uid)
            : undefined
    }

    const getAssetName = (assetUid: string | null | undefined) => {
        if (!assetUid) return t('list.no_asset')
        const asset = documentAssets.find(a => a.uid === assetUid)
        const lang = i18n.language?.split('-')[0] || 'en'
        return asset?.name ? (typeof asset.name === 'string' ? asset.name : asset.name[lang] || asset.name['en'] || asset.name['fr'] || t('list.unknown_asset')) : t('list.unknown_asset')
    }

    const getDocumentTypeName = (documentTypeUid: string | null | undefined) => {
        if (!documentTypeUid) return t('list.no_type')
        const documentType = documentTypes.find(dt => dt.uid === documentTypeUid)
        const lang = i18n.language?.split('-')[0] || 'en'
        return documentType?.name ? (typeof documentType.name === 'string' ? documentType.name : documentType.name[lang] || documentType.name['en'] || documentType.name['fr'] || documentType.code) : t('list.unknown_type')
    }

    // Note: Both assets and document types now use API-based search

    // API search handlers  
    const { request } = useApi()

    const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            // Always include "No asset" option
            const options: ApiSearchableSelectOption[] = [{ value: 'null', label: `🤔 ${t('list.no_asset')}` }]

            const queryParams: Record<string, string> = {
                limit: '50'
            }

            if (query && query.toLowerCase() !== 'no asset') {
                // When searching, sort by name and search all assets
                queryParams.q = query
                queryParams.sort = 'name'
            } else {
                // When not searching, sort by updated_at and filter to building types only
                queryParams.sort = '-updated_at'

                // Get asset type UIDs for building and building_group
                const assetTypesResponse = await request('/api/v1/organizations/{orgCode}/asset-types', 'GET', {
                    params: {
                        path: { orgCode: org }
                    }
                })

                const assetTypes = (assetTypesResponse as any)?.data || []
                const buildingTypes = assetTypes.filter((type: any) =>
                    type.code === 'building' || type.code === 'building_group'
                )
                const buildingTypeUIDs = buildingTypes.map((type: any) => type.uid).join(',')

                // Add asset type filter if we found building types
                if (buildingTypeUIDs) {
                    queryParams.asset_type_uid = buildingTypeUIDs
                }
            }

            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: {
                    path: { orgCode: org },
                    query: queryParams
                }
            })

            const assets = (response as any)?.data || []

            const assetOptions = assets.map((asset: any) => ({
                value: asset.uid,
                label: typeof asset.name === 'string' ? asset.name : asset.name?.['fr'] || asset.name?.['en'] || 'Unknown'
            }))

            return [...options, ...assetOptions]
        } catch (error) {
            console.error('Asset search error:', error)
            return [{ value: 'null', label: `🤔 ${t('list.no_asset')}` }]
        }
    }, [org, request, t])

    // Get initial asset options using the search function for proper sorting
    const [initialAssetOptions, setInitialAssetOptions] = useState<ApiSearchableSelectOption[]>([])

    useEffect(() => {
        const loadInitialAssets = async () => {
            try {
                // Load initial assets using empty search (which gives us sorted results)
                const options = await searchAssets('')
                setInitialAssetOptions([
                    { value: 'null', label: `🤔 ${t('list.no_asset')}` },
                    ...options.slice(0, 20) // Take first 20 for initial display
                ])
            } catch (error) {
                console.error('Failed to load initial assets:', error)
                setInitialAssetOptions([{ value: 'null', label: `🤔 ${t('list.no_asset')}` }])
            }
        }

        loadInitialAssets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAssets])

    // Load document types once and cache them for local filtering
    const [initialDocumentTypeOptions, setInitialDocumentTypeOptions] = useState<ApiSearchableSelectOption[]>([])

    useEffect(() => {
        const loadDocumentTypes = async () => {
            try {
                const response = await request('/api/v1/organizations/{orgCode}/document-types', 'GET', {
                    params: {
                        path: { orgCode: org },
                        query: {
                            limit: '1000' // Get all document types (they're typically limited in number)
                        }
                    }
                })

                const types = (response as any)?.data || []

                const lang = i18n.language?.split('-')[0] || 'en'
                const options = types.map((type: any) => ({
                    value: type.uid,
                    label: typeof type.name === 'string' ? type.name : type.name?.[lang] || type.name?.['en'] || type.name?.['fr'] || type.code || 'Unknown'
                }))

                setInitialDocumentTypeOptions([
                    { value: 'null', label: `🤔 ${t('list.no_type')}` },
                    ...options
                ])
            } catch (error) {
                console.error('Failed to load document types:', error)
                setInitialDocumentTypeOptions([{ value: 'null', label: `🤔 ${t('list.no_type')}` }])
            }
        }

        loadDocumentTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org, request, i18n.language])

    const searchDocumentTypes = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        // For document types, we do client-side filtering since we load all types upfront
        if (query && query.length >= 2) {
            const lowerQuery = query.toLowerCase()
            // Always include "No type" option if it exists
            const filtered = initialDocumentTypeOptions.filter((option: ApiSearchableSelectOption) =>
                option.label.toLowerCase().includes(lowerQuery)
            )
            // Ensure "No type" is included if searching for something that matches it
            if (lowerQuery.includes('no type') && !filtered.some(o => o.value === 'null')) {
                const noTypeOption = initialDocumentTypeOptions.find(o => o.value === 'null')
                if (noTypeOption) {
                    return [noTypeOption, ...filtered]
                }
            }
            return filtered
        }

        return initialDocumentTypeOptions
    }, [initialDocumentTypeOptions])

    // Bulk pickers must not offer the "no asset" / "no type" sentinel options —
    // a bulk change target is always a concrete UID. Reuse the list searches and
    // strip the null sentinel.
    const searchAssetsForBulk = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        const options = await searchAssets(query)
        return options.filter(o => o.value !== 'null')
    }, [searchAssets])

    const searchDocumentTypesForBulk = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        const options = await searchDocumentTypes(query)
        return options.filter(o => o.value !== 'null')
    }, [searchDocumentTypes])

    const formatFileSize = (bytes: number | undefined) => {
        if (!bytes) return t('list.unknown_size')
        const units = t('list.size_units', { returnObjects: true }) as string[]
        let size = bytes
        let unitIndex = 0
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024
            unitIndex++
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`
    }

    const getStatusIcon = (status: UploadFile['status']) => {
        switch (status) {
            case 'success':
                return <AppIcon path={mdiCheckCircle} size="lg" />
            case 'failed':
                return <AppIcon path={mdiAlertCircle} size="lg" className="text-destructive" />
            case 'uploading':
                return <AppIcon path={mdiProgressClock} size="lg" className="text-primary motion-safe:animate-spin" />
            case 'pending':
                return <AppIcon tone="muted" path={mdiProgressClock} size="lg" />
        }
    }

    const getStatusColor = (status: UploadFile['status']) => {
        switch (status) {
            case 'success':
                return 'text-foreground bg-muted/50'
            case 'failed':
                return 'text-destructive bg-destructive/10'
            case 'uploading':
                return 'text-primary bg-primary/10'
            case 'pending':
                return 'text-muted-foreground bg-muted/50'
        }
    }

    // One document row. Extracted so the flat table body and the folder
    // tree (single-asset filter, spec 2026-07-27-06) render identical rows.
    const renderDocumentRow = (document: typeof documents[number]) => {
        const rowIndex = displayIndexByUid.get(document.uid) ?? 0
        return (
                                <tr
                                    key={document.uid}
                                    className={selected.has(document.uid) ? 'bg-muted/50' : 'hover:bg-muted/50'}
                                    data-testid={`doc-row-${document.uid}`}
                                >
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <Checkbox
                                            checked={selected.has(document.uid)}
                                            onClick={(e) => { shiftKeyRef.current = e.shiftKey }}
                                            onChange={() => toggleRow(rowIndex, document.uid, shiftKeyRef.current)}
                                            aria-label={t('bulk.select_row')}
                                            data-testid={`doc-select-${document.uid}`}
                                        />
                                    </td>
                                    <td className="px-6 py-4 text-sm text-foreground">
                                        <Link
                                            to="/$org/documents/$docUid"
                                            params={{ org, docUid: document.uid }}
                                            search={splitFilterActive ? ({ review_split: true } as never) : undefined}
                                            className="flex items-center gap-3"
                                            data-testid={`doc-link-${document.uid}`}
                                        >
                                            <DocumentIcon
                                                document={document}
                                                org={org}
                                            />
                                            <span className="min-w-0">
                                                <span className="flex items-center gap-1.5">
                                                    <span className="block truncate max-w-[320px]">{document.name}</span>
                                                    {document.restricted && (
                                                        <RestrictedBadge testId={`doc-restricted-badge-${document.uid}`} />
                                                    )}
                                                    {(document as any).child_document_count > 0 && (
                                                        <span
                                                            className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 px-1 py-0.5 text-xs text-muted-foreground shrink-0"
                                                            data-testid={`doc-bundle-badge-${document.uid}`}
                                                            title={t('bundle.list_bundle_tooltip', { count: (document as any).child_document_count })}
                                                        >
                                                            <AppIcon path={mdiFileDocumentMultiple} size="xs" />
                                                            {(document as any).child_document_count}
                                                        </span>
                                                    )}
                                                    {(document as any).parent_document_uid && (
                                                        <span
                                                            className="inline-flex items-center shrink-0 text-muted-foreground"
                                                            data-testid={`doc-child-badge-${document.uid}`}
                                                            title={t('bundle.list_child_tooltip')}
                                                        >
                                                            <AppIcon path={mdiCallSplit} size="xs" />
                                                        </span>
                                                    )}
                                                    {((document as any).flags as string[] | undefined)?.includes('archive') && (
                                                        <span
                                                            className="inline-flex items-center gap-1 shrink-0 rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                                                            data-testid={`doc-archive-badge-${document.uid}`}
                                                            title={t('archive.list_badge_tooltip')}
                                                        >
                                                            <AppIcon path={mdiFolderZipOutline} size="xs" />
                                                            {t('archive.list_badge')}
                                                        </span>
                                                    )}
                                                </span>
                                                {((document as any).flags as string[] | undefined)?.includes('email') && (document.properties as any)?.email?.subject && (
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                                        <AppIcon path={mdiEmail} size="xs" />
                                                        <span className="truncate max-w-[200px]">{(document.properties as any).email.subject}</span>
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        <span className="inline-flex items-center gap-1.5">
                                            {getDocumentTypeName(document.document_type_uid)}
                                            {((document as any).flags as string[] | undefined)?.includes('document_type_proposal') && (
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-md border border-info/25 bg-info/10 px-1.5 py-0.5 text-xs text-info"
                                                    data-testid={`doc-proposal-badge-${document.uid}`}
                                                    title={t('list.badge_proposal')}
                                                >
                                                    <AppIcon path={mdiRobotOutline} size="xs" />
                                                    {t('list.badge_proposal')}
                                                </span>
                                            )}
                                            {!document.document_type_uid && (document.properties as any)?.suggested_document_type_uid && (
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-xs text-amber-600"
                                                    data-testid={`doc-suggestion-badge-${document.uid}`}
                                                    title={t('list.badge_suggestion')}
                                                >
                                                    <AppIcon path={mdiLightbulbOnOutline} size="xs" />
                                                    {t('list.badge_suggestion')}
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        {/*
                                          * A document's home is its asset and/or its contract
                                          * (spec 2026-08-13-01). A contract-only document renders
                                          * a contract chip here rather than "no asset", so it is
                                          * never shown as an orphan.
                                          */}
                                        {document.asset_uid ? (
                                            <Link
                                                to="/$org/assets/$assetUid"
                                                params={{ org, assetUid: document.asset_uid }}
                                                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 hover:underline"
                                            >
                                                <AssetTypeTile
                                                    type={getAssetTypeFor(document.asset_uid)}
                                                    size="sm"
                                                />
                                                {getAssetName(document.asset_uid)}
                                            </Link>
                                        ) : document.contract_uid ? (
                                            <Link
                                                to="/$org/contracts/$contractUid"
                                                params={{ org, contractUid: document.contract_uid }}
                                                className="text-primary hover:text-primary/80 hover:underline"
                                                data-testid={`document-contract-chip-${document.uid}`}
                                            >
                                                {document.contract_name || t('list.contract')}
                                            </Link>
                                        ) : (
                                            getAssetName(document.asset_uid)
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        {formatFileSize(document.file_size)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                aria-label={t('common:actions.view')}
                                                title={t('common:actions.view')}
                                                asChild
                                            >
                                                <Link
                                                    to="/$org/documents/$docUid"
                                                    params={{ org, docUid: document.uid }}
                                                    search={splitFilterActive ? ({ review_split: true } as never) : undefined}
                                                >
                                                    <AppIcon path={mdiEye} size="md" />
                                                </Link>
                                            </Button>
                                            <a
                                                href={`${apiBaseUrl}/api/v1/organizations/${org}/documents/${document.uid}/content?download=true`}
                                                download
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    aria-label={t('detail.download')}
                                                    title={t('detail.download')}
                                                >
                                                    <AppIcon path={mdiDownload} size="md" />
                                                </Button>
                                            </a>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setDocToDelete(document.uid)}
                                                aria-label={t('detail.delete', { defaultValue: 'Delete' })}
                                                title={t('detail.delete', { defaultValue: 'Delete' })}
                                                data-testid={`doc-delete-${document.uid}`}
                                                className="text-destructive hover:text-destructive/80"
                                            >
                                                <AppIcon path={mdiDelete} size="md" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
        )
    }

    return (
        <div
            className="space-y-6"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Processing blockers (spec 2026-08-18-01 §4): a paused document
                pipeline is invisible from the documents list otherwise. */}
            <ProcessingBlockersBanner org={org} />

            {/* Drag overlay */}
            {dragOver && (
                <div className="fixed inset-0 bg-primary/10 border-4 border-dashed border-primary z-50 flex items-center justify-center">
                    <div className="bg-card p-8 rounded-lg shadow-lg text-center border">
                        <div className="text-4xl mb-4">📄</div>
                        <div className="text-xl font-semibold text-foreground mb-2">{t('upload.drop_title')}</div>
                        <div className="text-sm text-muted-foreground">
                            {t('upload.drop_hint')}
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Interface Modal */}
            {showUploadInterface && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border">
                        <div className="p-6 border-b border-border">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-foreground">{t('upload_progress.title')}</h2>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        if (allUploadsComplete) {
                                            setShowUploadInterface(false)
                                            setUploadFiles([])
                                        }
                                    }}
                                    disabled={!allUploadsComplete}
                                >
                                    <AppIcon path={mdiClose} size="md" />
                                </Button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {uploadFiles.length === 0 ? (
                                <EmptyState variant="inline" title={t('upload_progress.no_files')} />
                            ) : (
                                <div className="space-y-3">
                                    {uploadFiles.map((uploadFile) => (
                                        <div
                                            key={uploadFile.id}
                                            className={`p-4 rounded-lg border ${getStatusColor(uploadFile.status)}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    {getStatusIcon(uploadFile.status)}
                                                    <div>
                                                        <p className="font-medium text-sm">{uploadFile.file.name}</p>
                                                        <p className="text-xs opacity-75">
                                                            {formatFileSize(uploadFile.file.size)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-medium capitalize">{uploadFile.status}</p>
                                                    {uploadFile.status === 'uploading' && (
                                                        <p className="text-xs" data-testid="upload-progress-phase">
                                                            {uploadFile.assembling
                                                                ? t('upload_progress.assembling')
                                                                : t('upload_progress.processing')}
                                                        </p>
                                                    )}
                                                    {uploadFile.retryCount > 0 && (
                                                        <p className="text-xs">{t('upload_progress.retry', { count: uploadFile.retryCount })}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadFile.error && (
                                                <p className="text-xs mt-2 text-destructive">{uploadFile.error}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-border">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('upload_progress.summary_success', { count: uploadFiles.filter(f => f.status === 'success').length })}
                                        {uploadFiles.filter(f => f.status === 'failed').length > 0 &&
                                            t('upload_progress.summary_failed', { count: uploadFiles.filter(f => f.status === 'failed').length })
                                        }
                                        {uploadFiles.filter(f => f.status === 'pending' || f.status === 'uploading').length > 0 &&
                                            t('upload_progress.summary_pending', { count: uploadFiles.filter(f => f.status === 'pending' || f.status === 'uploading').length })
                                        }
                                    </p>
                                </div>
                                {allUploadsComplete && (
                                    <Button onClick={() => {
                                        setShowUploadInterface(false)
                                        setUploadFiles([])
                                    }}>
                                        {t('common:actions.close')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PageHeader
                icon={SECTION_ICONS.documents}
                title={t('list.title')}
                description={t('list.subtitle')}
                actions={
                <>
                    <Button
                        onClick={() => setShowUploadForm(!showUploadForm)}
                        disabled={uploading}
                        aria-label={showUploadForm ? t('upload.cancel') : t('upload.button')}
                        title={showUploadForm ? t('upload.cancel') : t('upload.button')}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="upload-toggle-btn"
                    >
                        <AppIcon path={showUploadForm ? mdiClose : mdiUpload} size="md" className="md:mr-1" />
                        <span className="hidden md:inline">{showUploadForm ? t('upload.cancel') : t('upload.button')}</span>
                    </Button>
                    <Button
                        onClick={handleRefresh}
                        variant="outline"
                        disabled={loading}
                        aria-label={t('common:actions.refresh')}
                        title={t('common:actions.refresh')}
                    >
                        <AppIcon path={mdiRefresh} size="md" className="md:mr-1" />
                        <span className="hidden md:inline">{loading ? t('common:status.loading') : t('common:actions.refresh')}</span>
                    </Button>
                </>
                }
            />

            {/* Upload Form */}
            {showUploadForm && (
                <div className="bg-card p-6 rounded-lg border border-border">
                    <h2 className="text-lg font-semibold mb-4 text-foreground">{t('upload.title')}</h2>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="fileInput" className="block text-sm font-medium text-foreground mb-2">
                                {t('upload.select_files_label')}
                            </label>
                            <input
                                type="file"
                                id="fileInput"
                                ref={fileInputRef}
                                onChange={handleFileSelection}
                                className="block w-full px-3 py-2 border border-border bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                                accept="*/*"
                                multiple
                                data-testid="file-input"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                {t('upload.document_type_label')}
                            </label>
                            <ApiSearchableSelect
                                value={selectedDocumentTypeUid}
                                placeholder={t('upload.document_type_placeholder')}
                                searchPlaceholder={t('upload.document_type_search_placeholder')}
                                emptyMessage={t('upload.document_type_empty')}
                                onSearch={searchDocumentTypes}
                                initialOptions={initialDocumentTypeOptions}
                                onValueChange={(value) => setSelectedDocumentTypeUid(value || '')}
                                className="w-full"
                                minSearchLength={0}
                                debounceMs={150}
                            />
                        </div>

                        {/* Optional deposit session (spec 2026-08-16-05 §2) */}
                        <ImportSelectField
                            org={org}
                            value={selectedImportUid}
                            onChange={setSelectedImportUid}
                        />

                        {selectedFiles.length > 0 && (
                            <div className="text-sm text-muted-foreground">
                                {t('upload.selected_files', { count: selectedFiles.length })}
                                <ul className="mt-2 space-y-1">
                                    {selectedFiles.map(({ file, originalPath }, index) => (
                                        <li key={index} className="text-xs">
                                            • {originalPath || file.name} ({formatFileSize(file.size)})
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex gap-2">
                            <Button
                                onClick={handleUploadClick}
                                disabled={selectedFiles.length === 0}
                                data-testid="upload-button"
                            >
                                <AppIcon path={mdiUpload} size="md" className="mr-1" />
                                {selectedFiles.length > 0 ? t('upload.upload_count', { count: selectedFiles.length }) : t('upload.upload_files')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowUploadForm(false)
                                    setSelectedFiles([])
                                    setSelectedDocumentTypeUid('')
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = ''
                                    }
                                }}
                            >
                                {t('common:actions.cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Document List Filters — single row per list-page pattern §2;
                the selects' placeholders ("All assets" / "All types") carry
                the label, page size lives next to the count at the bottom. */}
            <div className="flex flex-wrap items-center gap-2">
                <ApiSearchableSelect
                    value={filterAssetUid}
                    placeholder={t('list.all_assets')}
                    searchPlaceholder={t('list.search_assets')}
                    emptyMessage={t('list.no_assets_found')}
                    onSearch={searchAssets}
                    initialOptions={initialAssetOptions}
                    onValueChange={(value) => navigate({
                        to: '/$org/documents',
                        params: { org },
                        search: {
                            asset_uid: value || undefined,
                            contract_uid: filterContractUid || undefined,
                            document_type_uid: filterDocumentTypeUid || undefined,
                            flags: filterFlags || undefined,
                            pii: filterPII,
                            import_uid: filterImportUid || undefined,
                            path_prefix: filterPathPrefix || undefined,
                            unclassified: filterUnclassified || undefined,
                            limit: filterLimit
                        }
                    })}
                    className="w-full sm:w-64"
                    minSearchLength={0}
                    debounceMs={150}
                />
                <ApiSearchableSelect
                    value={filterDocumentTypeUid}
                    placeholder={t('list.all_types')}
                    searchPlaceholder={t('list.search_document_types')}
                    emptyMessage={t('list.no_document_types_found')}
                    onSearch={searchDocumentTypes}
                    initialOptions={initialDocumentTypeOptions}
                    onValueChange={(value) => navigate({
                        to: '/$org/documents',
                        params: { org },
                        search: {
                            asset_uid: filterAssetUid || undefined,
                            contract_uid: filterContractUid || undefined,
                            document_type_uid: value || undefined,
                            flags: filterFlags || undefined,
                            pii: filterPII,
                            import_uid: filterImportUid || undefined,
                            path_prefix: filterPathPrefix || undefined,
                            unclassified: filterUnclassified || undefined,
                            limit: filterLimit
                        }
                    })}
                    className="w-full sm:w-64"
                    minSearchLength={0}
                    debounceMs={150}
                />
                {!filterToValidate && toValidateCount > 0 && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-destructive text-destructive hover:text-destructive"
                        data-testid="filter-to-validate-toggle"
                        onClick={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: (prev: Record<string, unknown>) => ({ ...prev, to_validate: true }) as never,
                        })}
                    >
                        <AppIcon path={mdiCheckDecagramOutline} size="sm" className="mr-1" />
                        {t('list.filter_to_validate', { count: toValidateCount })}
                    </Button>
                )}
                <Button
                    type="button"
                    variant={proposalFilterActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={toggleProposalFilter}
                    data-testid="filter-proposals-toggle"
                    aria-pressed={proposalFilterActive}
                >
                    <AppIcon path={mdiRobotOutline} size="sm" className="mr-1" />
                    {t('list.filter_pending_proposals')}
                </Button>
                <Button
                    type="button"
                    variant={splitFilterActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={toggleSplitFilter}
                    data-testid="filter-split-proposals-toggle"
                    aria-pressed={splitFilterActive}
                >
                    <AppIcon path={mdiCallSplit} size="sm" className="mr-1" />
                    {t('list.filter_split_proposals')}
                </Button>
                {/* Tri-state PII filter (spec 2026-08-13-02 §6), URL-routed like
                    every other filter here. Not a two-state toggle: "not
                    assessed" is a distinct, useful answer. */}
                <Select
                    value={filterPII ?? 'all'}
                    onValueChange={(value) => navigate({
                        to: '/$org/documents',
                        params: { org },
                        search: {
                            asset_uid: filterAssetUid || undefined,
                            contract_uid: filterContractUid || undefined,
                            document_type_uid: filterDocumentTypeUid || undefined,
                            flags: filterFlags || undefined,
                            pii: value === 'all' ? undefined : (value as 'true' | 'false' | 'unknown'),
                            import_uid: filterImportUid || undefined,
                            path_prefix: filterPathPrefix || undefined,
                            unclassified: filterUnclassified || undefined,
                            limit: filterLimit
                        }
                    })}
                >
                    <SelectTrigger
                        className="w-full sm:w-48 h-9"
                        aria-label={t('pii.filter.label', { ns: 'common' })}
                        data-testid="filter-pii-select"
                    >
                        <SelectValue placeholder={t('pii.filter.all', { ns: 'common' })} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all" data-testid="filter-pii-all">{t('pii.filter.all', { ns: 'common' })}</SelectItem>
                        <SelectItem value="true" data-testid="filter-pii-true">{t('pii.filter.true', { ns: 'common' })}</SelectItem>
                        <SelectItem value="false" data-testid="filter-pii-false">{t('pii.filter.false', { ns: 'common' })}</SelectItem>
                        <SelectItem value="unknown" data-testid="filter-pii-unknown">{t('pii.filter.unknown', { ns: 'common' })}</SelectItem>
                    </SelectContent>
                </Select>
                {/*
                  * The contract filter is URL-driven (a contract chip on a row,
                  * or a link from the contract page). It gets a visible,
                  * clearable chip rather than its own picker — grouping the
                  * whole page by contract is deliberately out of scope for v1.
                  */}
                {filterContractUid && (
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        data-testid="filter-contract-clear"
                        onClick={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: {
                                asset_uid: filterAssetUid || undefined,
                                contract_uid: undefined,
                                document_type_uid: filterDocumentTypeUid || undefined,
                                flags: filterFlags || undefined,
                                pii: filterPII,
                                import_uid: filterImportUid || undefined,
                                path_prefix: filterPathPrefix || undefined,
                                unclassified: filterUnclassified || undefined,
                                limit: filterLimit
                            }
                        })}
                    >
                        {t('list.filtered_by_contract', { name: documents[0]?.contract_name || t('list.contract') })}
                        <AppIcon path={mdiClose} size="sm" className="ml-1" />
                    </Button>
                )}
                {/* Import drill-down chip (spec 2026-08-16-05 §4): the funnel
                    numbers navigate here, so the filter has to be visible and
                    clearable rather than an invisible URL parameter. */}
                {filterImportUid && (
                    <ImportFilterBadge
                        org={org}
                        importUid={filterImportUid}
                        onClear={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: {
                                asset_uid: filterAssetUid || undefined,
                                contract_uid: filterContractUid || undefined,
                                document_type_uid: filterDocumentTypeUid || undefined,
                                flags: filterFlags || undefined,
                                pii: filterPII,
                                import_uid: undefined,
                                path_prefix: filterPathPrefix || undefined,
                                unclassified: filterUnclassified || undefined,
                                limit: filterLimit
                            }
                        })}
                    />
                )}
                {/* Deposit-folder chip (spec 2026-08-16-07 §2): arriving from
                    the import dashboard's tree, the user must see WHICH folder
                    narrowed the list — and be able to widen back out. */}
                {filterPathPrefix && (
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        data-testid="filter-path-prefix-clear"
                        title={filterPathPrefix}
                        onClick={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: {
                                asset_uid: filterAssetUid || undefined,
                                contract_uid: filterContractUid || undefined,
                                document_type_uid: filterDocumentTypeUid || undefined,
                                flags: filterFlags || undefined,
                                pii: filterPII,
                                import_uid: filterImportUid || undefined,
                                path_prefix: undefined,
                                unclassified: filterUnclassified || undefined,
                                limit: filterLimit
                            }
                        })}
                    >
                        {t('list.filtered_by_folder', { path: filterPathPrefix })}
                        <AppIcon path={mdiClose} size="sm" className="ml-1" />
                    </Button>
                )}
                {filterToValidate && (
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        data-testid="filter-to-validate-clear"
                        onClick={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: (prev: Record<string, unknown>) => ({ ...prev, to_validate: undefined }) as never,
                        })}
                    >
                        {t('list.filtered_by_to_validate')}
                        <AppIcon path={mdiClose} size="sm" className="ml-1" />
                    </Button>
                )}
                {filterUnclassified && (
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        data-testid="filter-unclassified-clear"
                        onClick={() => navigate({
                            to: '/$org/documents',
                            params: { org },
                            search: {
                                asset_uid: filterAssetUid || undefined,
                                contract_uid: filterContractUid || undefined,
                                document_type_uid: filterDocumentTypeUid || undefined,
                                flags: filterFlags || undefined,
                                pii: filterPII,
                                import_uid: filterImportUid || undefined,
                                path_prefix: filterPathPrefix || undefined,
                                unclassified: undefined,
                                limit: filterLimit
                            }
                        })}
                    >
                        {t('list.filtered_by_unclassified')}
                        <AppIcon path={mdiClose} size="sm" className="ml-1" />
                    </Button>
                )}
            </div>

            {/* Non-blocking notification */}
            {notice && (
                <div
                    role="status"
                    data-testid="bulk-notice"
                    className={`flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm ${notice.tone === 'error'
                        ? 'border-destructive/40 bg-destructive/10 text-destructive'
                        : 'border-primary/40 bg-primary/10 text-foreground'}`}
                >
                    <span>{notice.message}</span>
                    <button
                        type="button"
                        onClick={() => setNotice(null)}
                        aria-label={t('common:actions.close')}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="bulk-notice-close"
                    >
                        <AppIcon path={mdiClose} size="sm" />
                    </button>
                </div>
            )}

            {/* Bulk actions bar */}
            <BulkActionsBar
                count={selected.size}
                label={t('bulk.selected_count', { count: selected.size })}
                onClear={clearSelection}
                busy={bulkMutation.isPending}
                data-testid="bulk-bar"
            >
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteOpen(true)}
                    disabled={bulkMutation.isPending}
                    aria-label={t('bulk.delete')}
                    title={t('bulk.delete')}
                    data-testid="bulk-delete-btn"
                >
                    <AppIcon path={mdiDelete} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('bulk.delete')}</span>
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBulkTypeOpen(true)}
                    disabled={bulkMutation.isPending}
                    aria-label={t('bulk.change_type')}
                    title={t('bulk.change_type')}
                    data-testid="bulk-change-type-btn"
                >
                    <AppIcon path={mdiSwapHorizontal} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('bulk.change_type')}</span>
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBulkAssetOpen(true)}
                    disabled={bulkMutation.isPending}
                    aria-label={t('bulk.change_asset')}
                    title={t('bulk.change_asset')}
                    data-testid="bulk-change-asset-btn"
                >
                    <AppIcon path={mdiFolderMove} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('bulk.change_asset')}</span>
                </Button>
            </BulkActionsBar>

            {/* Error Display */}
            {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                    <div className="text-destructive text-sm">{error}</div>
                </div>
            )}

            {/* Documents Table — inner horizontal scroll on narrow viewports
                so the page body never scrolls sideways */}
            <Card className="overflow-hidden overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="px-4 py-3 text-left">
                                <Checkbox
                                    ref={headerCheckboxRef}
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    disabled={documents.length === 0}
                                    aria-label={t('bulk.select_all')}
                                    title={t('bulk.select_all')}
                                    data-testid="bulk-select-all"
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {t('list.column_document')}
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {t('list.column_type')}
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {t('list.column_asset')}
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {t('list.column_size')}
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                {t('list.column_actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-4 text-center text-muted-foreground">
                                    {t('list.loading')}
                                </td>
                            </tr>
                        ) : documents.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6">
                                    <EmptyState
                                        variant="inline"
                                        live
                                        icon={SECTION_ICONS.documents}
                                        title={t('list.empty')}
                                    />
                                </td>
                            </tr>
                        ) : (
                            folderModeActive ? (
                                <DocumentFolderTree
                                    folders={folders}
                                    documents={documents as unknown as GroupableDocument[]}
                                    renderFolder={({ folder, depth, count, label, number, expanded, onToggle }) => (
                                        <tr
                                            key={`folder-${folder.uid}`}
                                            className="bg-muted/40"
                                            data-testid={folder.uid === 'unclassified'
                                                ? 'documents-folder-unclassified'
                                                : `documents-folder-${folder.uid}`}
                                        >
                                            <td colSpan={7} className="px-4 py-2 text-sm font-medium text-foreground">
                                                {/* Collapse state lives in DocumentFolderTree, so this
                                                    custom row renderer only has to draw the toggle. */}
                                                <button
                                                    type="button"
                                                    onClick={onToggle}
                                                    aria-expanded={expanded}
                                                    className="inline-flex items-center gap-2 text-left"
                                                    style={{ paddingLeft: depth * 16 }}
                                                    data-testid={folder.uid === 'unclassified'
                                                        ? 'documents-folder-toggle-unclassified'
                                                        : `documents-folder-toggle-${folder.uid}`}
                                                >
                                                    <AppIcon tone="muted" path={expanded ? mdiChevronDown : mdiChevronRight} size="sm" />
                                                    {/* The unclassified bucket is not in the layout, so it carries no number. */}
                                                    {number && (
                                                        <span
                                                            className="text-xs text-muted-foreground font-mono shrink-0 tabular-nums"
                                                            data-testid={`documents-folder-number-${folder.uid}`}
                                                        >
                                                            {number}
                                                        </span>
                                                    )}
                                                    <AppIcon tone="muted" path={folder.uid === 'unclassified' ? mdiFolderQuestionOutline : expanded ? mdiFolderOpenOutline : mdiFolderOutline} size="sm" />
                                                    {label}
                                                    {/* PII marker — the folder's document-type subtree
                                                        holds at least one `pii` type (spec 2026-08-13-02 §8). */}
                                                    {folder.pii && (
                                                        <AppIcon
                                                            path={mdiShieldAccount}
                                                            size="xs"
                                                            className="text-warning shrink-0"
                                                            title={t('pii.folder_marker', { ns: 'common' })}
                                                            data-testid={`documents-folder-pii-${folder.uid}`}
                                                        />
                                                    )}
                                                    <span className="text-xs text-muted-foreground">{count}</span>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    renderDocument={(doc) => renderDocumentRow(doc as unknown as typeof documents[number])}
                                />
                            ) : (
                                displayedDocuments.map(document => renderDocumentRow(document))
                            )
                        )}
                    </tbody>
                </table>
            </Card>

            {/* Documents count + page size — pagination concerns live together
                under the table, not among the content filters */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                {documents.length > 0 ? (
                    <p data-testid="documents-count">
                        {typeof totalDocuments === 'number' && totalDocuments > documents.length
                            ? t('list.count_truncated', { count: documents.length, total: totalDocuments })
                            : t('list.count', { count: documents.length })}
                    </p>
                ) : <span />}
                <Select
                    value={String(filterLimit)}
                    onValueChange={(value) => navigate({
                        to: '/$org/documents',
                        params: { org },
                        search: {
                            asset_uid: filterAssetUid || undefined,
                            contract_uid: filterContractUid || undefined,
                            document_type_uid: filterDocumentTypeUid || undefined,
                            flags: filterFlags || undefined,
                            pii: filterPII,
                            import_uid: filterImportUid || undefined,
                            path_prefix: filterPathPrefix || undefined,
                            unclassified: filterUnclassified || undefined,
                            limit: Number(value)
                        }
                    })}
                >
                    <SelectTrigger
                        className="w-auto min-w-[80px] h-8"
                        aria-label={t('list.page_size_label')}
                        title={t('list.page_size_label')}
                        data-testid="documents-page-size-select"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PAGE_SIZE_OPTIONS.map(size => (
                            <SelectItem key={size} value={String(size)} data-testid={`documents-page-size-${size}`}>
                                {t('list.page_size_option', { count: size })}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Bulk delete confirmation */}
            <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
                <AlertDialogContent data-testid="bulk-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('bulk.confirm_delete_title', { count: selected.size })}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('bulk.confirm_delete_description', { count: selected.size })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkMutation.isPending}>
                            {t('common:actions.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleBulkDelete() }}
                            disabled={bulkMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="bulk-delete-confirm"
                        >
                            {t('bulk.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Single-row delete confirmation */}
            <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null) }}>
                <AlertDialogContent data-testid="doc-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('detail.delete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('detail.confirm_delete')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>
                            {t('common:actions.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDelete() }}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="doc-delete-confirm"
                        >
                            {t('detail.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Bulk change-type dialog */}
            <Dialog open={bulkTypeOpen} onOpenChange={(open) => { setBulkTypeOpen(open); if (!open) setBulkTypeValue('') }}>
                <DialogContent data-testid="bulk-change-type-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('bulk.change_type')}</DialogTitle>
                        <DialogDescription>
                            {t('bulk.change_type_description', { count: selected.size })}
                        </DialogDescription>
                    </DialogHeader>
                    <ApiSearchableSelect
                        value={bulkTypeValue}
                        placeholder={t('list.all_types')}
                        searchPlaceholder={t('list.search_document_types')}
                        emptyMessage={t('list.no_document_types_found')}
                        onSearch={searchDocumentTypesForBulk}
                        initialOptions={initialDocumentTypeOptions.filter(o => o.value !== 'null')}
                        onValueChange={setBulkTypeValue}
                        className="w-full"
                        minSearchLength={0}
                        debounceMs={150}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setBulkTypeOpen(false); setBulkTypeValue('') }} disabled={bulkMutation.isPending}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleBulkChangeType}
                            disabled={!bulkTypeValue || bulkMutation.isPending}
                            data-testid="bulk-change-type-apply"
                        >
                            {t('bulk.apply')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk change-asset dialog */}
            <Dialog open={bulkAssetOpen} onOpenChange={(open) => { setBulkAssetOpen(open); if (!open) setBulkAssetValue('') }}>
                <DialogContent data-testid="bulk-change-asset-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('bulk.change_asset')}</DialogTitle>
                        <DialogDescription>
                            {t('bulk.change_asset_description', { count: selected.size })}
                        </DialogDescription>
                    </DialogHeader>
                    <ApiSearchableSelect
                        value={bulkAssetValue}
                        placeholder={t('list.all_assets')}
                        searchPlaceholder={t('list.search_assets')}
                        emptyMessage={t('list.no_assets_found')}
                        onSearch={searchAssetsForBulk}
                        initialOptions={initialAssetOptions.filter(o => o.value !== 'null')}
                        onValueChange={setBulkAssetValue}
                        className="w-full"
                        minSearchLength={0}
                        debounceMs={150}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setBulkAssetOpen(false); setBulkAssetValue('') }} disabled={bulkMutation.isPending}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleBulkChangeAsset}
                            disabled={!bulkAssetValue || bulkMutation.isPending}
                            data-testid="bulk-change-asset-apply"
                        >
                            {t('bulk.apply')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}